import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import {
  setIsChangingSeat,
  setOriginalEventId,
  setOriginalTickets,
  setSelectedEvent,
  setSelectedPartySize,
  setSelectedSubscription,
} from "features/slices/reservationFlowSlice";
import { EventAvailabilityDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDeferredSeatAssignment } from "hooks/useDeferredSeatAssignment";
import useSubscriptionOperable from "hooks/useSubscriptionOperable";
import useTDCDialog from "hooks/useTDCDialog";
import { MainFlowParamList } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useEffect, useMemo } from "react";
import { ActivityIndicator, View } from "react-native";
import RSVPDialog from "screens/Schedule/components/RSVPDialog";
import TDCDialog from "screens/Schedule/components/TDCDialog";
import ScheduleItemList from "screens/Schedule/ScheduleItemList";
import { getUniqSubscriptions } from "utils/subscriptionUtils";

const UpcomingGames = () => {
  const { TDCDialogVisible, setTDCDialogVisible, withTDCCheck } = useTDCDialog();
  const {
    rsvpDialogVisible,
    setRsvpDialogVisible,
    rsvpEvent,
    handleDeferredSeatAssignment,
  } = useDeferredSeatAssignment();
  const { account } = useAccount();
  const dispatch = useAppDispatch();
  const { selectedSubscription, selectedEvent } = useAppSelector(
    (state) => state.reservationFlow,
  );

  const {
    data: schedule,
    refetch,
    isFetching,
  } = useFetchEventAvailablitiesQuery({
    accountId: account!.id,
    planPackageId: selectedSubscription?.planPackage.id,
  });

  const navigation = useNavigation<NavigationProp<MainFlowParamList>>();
  const uniqSubscriptions = useMemo(() => {
    if (!account) return [];
    return getUniqSubscriptions(account.subscriptions);
  }, [account]);

  const firstFiveHomeGames = useMemo(() => {
    return schedule ? schedule.slice(0, 5) : [];
  }, [schedule]);

  const subscriptionOperative = useSubscriptionOperable(selectedSubscription);

  const onScheduleItemClick = (item: EventAvailabilityDetails) => {
    dispatch(setSelectedEvent(item));

    handleDeferredSeatAssignment(item, () => {
      withTDCCheck(item, () => {
        dispatch(setOriginalTickets([]));
        dispatch(setIsChangingSeat(false));
        dispatch(setOriginalEventId(undefined));
        dispatch(setSelectedPartySize(0));

        navigation.navigate("Schedule", {
          screen: "SeatSelection",
          initial: false,
        });
      });
    });
  };
  // On page loads, set selected subscription to the first one available
  // return if already set
  useEffect(() => {
    if (uniqSubscriptions.length === 0 || selectedSubscription !== null) return;
    dispatch(setSelectedSubscription(uniqSubscriptions[0]));
  }, [uniqSubscriptions]);

  // On selected subscription change, fetch evetn availablities
  useEffect(() => {
    if (!selectedSubscription) return;
    refetch();
  }, [selectedSubscription]);

  if (!schedule) return null;

  return (
    <View>
      {selectedEvent && (
        <TDCDialog
          visible={TDCDialogVisible}
          setVisible={setTDCDialogVisible}
          cancelText="CLOSE"
          event={selectedEvent}
        />
      )}

      {rsvpEvent && selectedSubscription && (
        <RSVPDialog
          visible={rsvpDialogVisible}
          setVisible={setRsvpDialogVisible}
          event={rsvpEvent}
          planPackageId={selectedSubscription.planPackage.id}
          selectedSubscription={selectedSubscription}
        />
      )}
      {schedule.length > 0 && (
        <View>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 15,
            }}
          >
            <Text category="h6">Upcoming:</Text>
            {isFetching && <ActivityIndicator />}
            <Button
              onPress={() =>
                navigation.navigate("Schedule", {
                  screen: "ScheduleScreen",
                })
              }
              appearance="ghost"
            >
              {`${i18n.t("screens.Dashboard.viewMore")} >`}
            </Button>
          </View>
          <ScheduleItemList
            schedule={firstFiveHomeGames}
            selectedSubscription={selectedSubscription}
            planPackageOperable={subscriptionOperative}
            onScheduleItemClick={onScheduleItemClick}
          />
        </View>
      )}
    </View>
  );
};

export default UpcomingGames;
